/**
 * skillLedger.js — Build 1 · the canonical device-local skill ledger.
 * ────────────────────────────────────────────────────────────────
 * ONE place every learning surface records "the child attempted skill X
 * and got it right/wrong". All surfaces (Reading Quest, Maths Academy,
 * Quest Academy, Treasure Quest, Language Quest) can write here without
 * knowing about each other.
 *
 * Design goals (from the Build 1 brief):
 *   • device-local, no accounts, no PII
 *   • versioned + migration-safe (never destroy prior progress)
 *   • append-only event log + a fast aggregate mirror for first paint
 *   • export/import-capable shape for future portability/sync
 *   • storage engine is swappable — callers use the API, not the keys,
 *     so an IndexedDB backing can replace localStorage later without
 *     touching a single call site.
 *
 * Skill IDs are NAMESPACED strings: "<surface>:<skill>", e.g.
 *   reading:inference · maths:place-value · academy:geo-seed-1
 * Keep the namespace stable so cross-surface mapping stays coherent.
 */

export const LEDGER_VERSION = 1;

const K_AGG = "we_skill_ledger_v1";       // aggregate mirror (fast first paint)
const K_EVENTS = "we_skill_events_v1";    // append-only event log (capped)
const K_VER = "we_skill_ledger_version";
const LEGACY_MASTERY = "we_mastery_v1";   // pre-Build-1 mastery store

const EVENTS_CAP = 600;                    // keep the log bounded on-device

const safeGet = (k) => { try { return localStorage.getItem(k); } catch { return null; } };
const safeSet = (k, v) => { try { localStorage.setItem(k, v); } catch { /* quota */ } };
const parse = (raw, def) => { try { return JSON.parse(raw ?? "null") ?? def; } catch { return def; } };
const nowIso = () => new Date().toISOString();
const uid = () => {
  try { return crypto.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`; }
  catch { return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`; }
};

const emit = (detail) => {
  try { window.dispatchEvent(new CustomEvent("we-skill-ledger-changed", { detail })); }
  catch { /* SSR/tests */ }
};

/* ── storage helpers ─────────────────────────────────────────── */
function readAgg() { return parse(safeGet(K_AGG), {}); }
function writeAgg(agg) { safeSet(K_AGG, JSON.stringify(agg)); }
function readEvents() { return parse(safeGet(K_EVENTS), []); }
function writeEvents(list) {
  safeSet(K_EVENTS, JSON.stringify(list.slice(-EVENTS_CAP)));
}

/* ── migration (runs once, never destructive) ────────────────── */
let _migrated = false;
function ensureMigrated() {
  if (_migrated) return;
  _migrated = true;
  const ver = Number(safeGet(K_VER) || 0);
  if (ver >= LEDGER_VERSION) return;
  const agg = readAgg();
  // Seed aggregates from the legacy mastery store so prior progress is
  // preserved. Legacy shape: { skillId: { state, times, lastAt } }.
  const legacy = parse(safeGet(LEGACY_MASTERY), {});
  Object.entries(legacy).forEach(([skillId, v]) => {
    if (agg[skillId]) return; // never overwrite newer canonical data
    const times = Number(v?.times || 0);
    agg[skillId] = {
      skillId,
      attempts: times,
      correct: v?.state === "mastered" ? times : Math.round(times * 0.6),
      streak: v?.state === "mastered" ? 2 : 0,
      lastAt: v?.lastAt || null,
      state: v?.state === "mastered" ? "mastered" : (times > 0 ? "practising" : "learning"),
      skillType: null,
      migratedFromLegacy: true,
    };
  });
  writeAgg(agg);
  safeSet(K_VER, String(LEDGER_VERSION));
}

/* ── mastery state rule (simple, explainable) ────────────────── */
export function accuracy(agg) {
  if (!agg || !agg.attempts) return null;
  return agg.correct / agg.attempts;
}
function deriveState(agg) {
  const acc = accuracy(agg) ?? 0;
  if (agg.attempts >= 4 && acc >= 0.8 && agg.streak >= 2) return "mastered";
  if (agg.attempts >= 2) return "practising";
  return "learning";
}

/* ── Transparent 4-level mastery model (Oct 2026) ───────────────
 * Mirrors the server Mastery Service vocabulary (school_os.THRESHOLDS: secure 80 / developing 60 /
 * min_evidence 2). Additive: `state` (learning/practising/mastered) is kept for existing consumers.
 *   not_yet      — no attempts recorded
 *   insufficient — fewer than 2 attempts: "insufficient to assess", never shown as a judgement
 *   developing   — ≥2 attempts, accuracy < 80% (or no recent streak)
 *   proficient   — ≥4 attempts, accuracy ≥ 80%, 2+ in a row
 *   mastered     — ≥8 attempts, accuracy ≥ 85%, 3+ in a row, evidence spread over 2+ days
 * Opening or finishing an activity never counts — only answered items reach the ledger. */
export const LEVELS = ["not_yet", "insufficient", "developing", "proficient", "mastered"];
export const LEVEL_LABEL = { not_yet: "Not yet demonstrated", insufficient: "Insufficient to assess", developing: "Developing understanding", proficient: "Proficient", mastered: "Consistent mastery" };
export function deriveLevel(agg) {
  if (!agg || !agg.attempts) return "not_yet";
  if (agg.attempts < 2) return "insufficient";
  const acc = accuracy(agg) ?? 0;
  const days = agg.firstAt && agg.lastAt ? (new Date(agg.lastAt) - new Date(agg.firstAt)) / 864e5 : 0;
  if (agg.attempts >= 8 && acc >= 0.85 && (agg.streak || 0) >= 3 && days >= 1) return "mastered";
  if (agg.attempts >= 4 && acc >= 0.8 && (agg.streak || 0) >= 2) return "proficient";
  return "developing";
}
export function levelOf(skillId) { return deriveLevel(getSkill(skillId)); }
export function countByLevel(level, namespace = null) {
  return Object.values(getAllSkills() || {}).filter((a) => deriveLevel(a) === level && (!namespace || a.skillId.startsWith(namespace + ":") || a.skillType === namespace)).length;
}

/* ── public API ──────────────────────────────────────────────── */

/**
 * Record one skill attempt. Idempotent when `eventId` is supplied and
 * already present in the recent log. Returns the updated aggregate.
 */
export function recordSkillEvent({
  skillId, correct, kind = "practice", skillType = null,
  durationMs = 0, eventId = null, meta = null,
}) {
  if (!skillId || typeof skillId !== "string") return null;
  ensureMigrated();

  const events = readEvents();
  const id = eventId || uid();
  if (eventId && events.some((e) => e.id === eventId)) {
    // Already recorded — return current aggregate unchanged.
    return getSkill(skillId);
  }
  events.push({
    id, skillId, correct: !!correct, kind,
    skillType: skillType || null, ts: nowIso(),
    ...(durationMs ? { durationMs } : {}),
  });
  writeEvents(events);

  const agg = readAgg();
  const cur = agg[skillId] || {
    skillId, attempts: 0, correct: 0, streak: 0, lastAt: null,
    state: "learning", skillType: skillType || null,
  };
  cur.attempts += 1;
  if (correct) { cur.correct += 1; cur.streak = (cur.streak || 0) + 1; }
  else { cur.streak = 0; cur.wrong = (cur.wrong || 0) + 1; cur.lastWrongAt = nowIso(); }
  cur.lastAt = nowIso();
  if (!cur.firstAt) cur.firstAt = cur.lastAt;
  if (skillType && !cur.skillType) cur.skillType = skillType;
  if (meta) cur.meta = { ...(cur.meta || {}), ...meta };
  cur.state = deriveState(cur);
  cur.level = deriveLevel(cur);
  agg[skillId] = cur;
  writeAgg(agg);

  emit({ skillId, state: cur.state });
  return cur;
}

/** One skill's aggregate, or null. */
export function getSkill(skillId) {
  ensureMigrated();
  return readAgg()[skillId] || null;
}

/** Whole aggregate map (canonical read for first paint). */
export function getAllSkills() {
  ensureMigrated();
  return readAgg();
}

/** Count of skills in a given state, optionally within a namespace. */
export function countByState(state, namespace = null) {
  const agg = getAllSkills();
  return Object.values(agg).filter(
    (s) => s.state === state && (!namespace || String(s.skillId).startsWith(`${namespace}:`)),
  ).length;
}

/**
 * Weak skills that would benefit from review — enough attempts but low
 * accuracy and not mastered. Sorted weakest-first. `namespace` filters
 * to one surface (e.g. "reading").
 */
export function getWeakSkills({ namespace = null, minAttempts = 3, maxAccuracy = 0.6, limit = 8 } = {}) {
  const agg = getAllSkills();
  return Object.values(agg)
    .filter((s) => (!namespace || String(s.skillId).startsWith(`${namespace}:`)))
    .filter((s) => s.state !== "mastered" && s.attempts >= minAttempts && (accuracy(s) ?? 1) <= maxAccuracy)
    .sort((a, b) => (accuracy(a) ?? 1) - (accuracy(b) ?? 1))
    .slice(0, limit);
}

/** Recent events (newest last), optionally for one skill. */
export function getEvents({ skillId = null, limit = 50 } = {}) {
  ensureMigrated();
  const list = readEvents();
  const filtered = skillId ? list.filter((e) => e.skillId === skillId) : list;
  return filtered.slice(-limit);
}

/** Export the full ledger (for future portability / manual backup). */
export function exportLedger() {
  ensureMigrated();
  return {
    version: LEDGER_VERSION,
    exportedAt: nowIso(),
    aggregate: readAgg(),
    events: readEvents(),
  };
}

/** Import a previously exported ledger (merge, never clobber newer). */
export function importLedger(payload) {
  if (!payload || typeof payload !== "object") return false;
  ensureMigrated();
  const agg = readAgg();
  Object.entries(payload.aggregate || {}).forEach(([id, v]) => {
    const cur = agg[id];
    if (!cur || (v.attempts || 0) > (cur.attempts || 0)) agg[id] = v;
  });
  writeAgg(agg);
  if (Array.isArray(payload.events)) {
    const seen = new Set(readEvents().map((e) => e.id));
    const merged = [...readEvents(), ...payload.events.filter((e) => !seen.has(e.id))];
    merged.sort((a, b) => String(a.ts).localeCompare(String(b.ts)));
    writeEvents(merged);
  }
  safeSet(K_VER, String(LEDGER_VERSION));
  emit({ imported: true });
  return true;
}

export const LEDGER_KEYS = [K_AGG, K_EVENTS, K_VER];
